<template>
  <div class="sidebar">
    <!-- 新增按钮 -->
    <div class="action-box">
      <el-button type="primary" @click="handleSave">
        <el-icon><Check /></el-icon>保存
      </el-button>
    </div>

    <!-- 菜单列表 -->
    <div class="menu-container" @click="contextMenuVisible = false">
      <el-menu
        :default-active="menuTemp.id"
        :default-openeds="defaultOpeneds"
        class="menu-tree"
        :collapse-transition="false"
      >
        <menu-item-component
          v-for="item in filteredMenuList"
          :key="item.id"
          :item="item"
          :input-name="menuTemp.name"
          @update:item="handleUpdateItem"
          @update:inputName="updateInputName"
          @save="handleSave"
          @cancel="handleCancel"
          @context-menu="handleContextMenu"
          @remove="handleRemoveNewItem"
          @select="handleSelect"
          @refresh-data="handleRefreshData"
        />
      </el-menu>
    </div>
    <!-- 在Sidebar组件的template中添加 -->
    <div ref="menuRef">
      <mini-opt-menu
        v-model:visible="contextMenuVisible"
        :position="contextMenuPosition"
        :current-item="currentContextItem"
        :menu-type="currentContextItem?.type"
        :id="currentContextItem?.id"
        :standard-id="currentContextItem?.standardId?.toString()"
        :level="currentContextItem?.level ?? 1"
        :num-id="currentContextItem?.numId"
        :menu-list="menuList"
        :level-count="levelCount"
        :first-level-count="firstLevelCount"
        @mini-menu="handleMiniMenuAction"
        @refresh="handleRefresh"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import MenuItemComponent from "@/components/MenuItemComponent.vue";
import MiniOptMenu from "@/components/miniOptMenu.vue";
import { type MenuItem } from "@/types/menu";
import { ElMessage } from "element-plus";
import { getLevelDetail } from "@/api/level";

// 新增右键菜单相关状态
const contextMenuVisible = ref(false);
const contextMenuPosition = ref({ left: 0, top: 0 });
const currentContextItem = ref<MenuItem | null>(null);
// 计算属性：统计所有 level 类型且 level 为 1 的菜单项数量
const firstLevelCount = computed(() => {
  let count = 0;

  const countFirstLevels = (items: MenuItem[]) => {
    for (const item of items) {
      // 注意：这里我们只统计直接的一级指标（level 为 1 且类型为 level）
      if (item.type === "level" && item.level === 1) {
        count++;
      }
      // 递归子项，因为一级指标可能嵌套在标准项下
      if (item.children?.length) {
        countFirstLevels(item.children);
      }
    }
  };

  countFirstLevels(props.menuList);
  return count;
});
// 添加计算属性：统计所有 level 类型的菜单项数量
const levelCount = computed(() => {
  let count = 0;

  const countLevels = (items: MenuItem[]) => {
    for (const item of items) {
      if (item.type === "level") {
        count++;
      }
      if (item.children?.length) {
        countLevels(item.children);
      }
    }
  };

  countLevels(props.menuList);
  return count;
});

// 修改 handleContextMenu 方法
const handleContextMenu = async (event: MouseEvent, item: MenuItem) => {
  event.preventDefault();
  event.stopPropagation();

  if (item.type !== "level") {
    return;
  }

  const targetElement = event.target as HTMLElement;
  const response = await getLevelDetail(item.id, item.level);

  if (response?.code === 1) {
    currentContextItem.value = {
      ...item,
      ...response.data,
      standardId: String(response.data.standardId || item.standardId), // 确保使用最新的 standardId
    };
  } else {
    console.warn("获取级别详情失败:", response);
  }

  // 使用点击坐标而不是元素坐标
  const clickX = event.clientX;
  const clickY = event.clientY;

  contextMenuPosition.value = {
    x: clickX,
    y: clickY,
  };
  contextMenuVisible.value = true;

  console.log("右键菜单项类型:", item.type);
};

// 修改点击外部关闭菜单的实现
const handleClickOutside = (event: MouseEvent) => {
  // 检查点击是否在菜单外部
  const menuContainer = document.querySelector(".mini-opt-menu");
  const clickedElement = event.target as HTMLElement;

  if (menuContainer && !menuContainer.contains(clickedElement)) {
    contextMenuVisible.value = false;
  }
};

// 在组件挂载时添加事件监听
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

// 在组件卸载前移除事件监听
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

// 添加全局点击监听关闭菜单
document.addEventListener("click", () => {
  contextMenuVisible.value = false;
});

// Props 定义
const props = defineProps<{
  menuList: MenuItem[];
  menuTemp: MenuItem;
}>();

// 事件定义
const emit = defineEmits<{
  "update:menu-temp": [value: MenuItem];
  "update:menu-list": [value: MenuItem[]];
  "context-menu": [event: MouseEvent, item: MenuItem];
  "menu-action": [action: { type: string; data?: any; id?: string }];
}>();

const searchText = ref("");

// 搜索过滤
const filteredMenuList = computed(() => {
  if (!searchText.value) return props.menuList;

  const searchLower = searchText.value.toLowerCase();

  const searchInItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter((item) => {
      // 检查当前项名称
      const nameMatch =
        (item.name || "").toLowerCase().includes(searchLower) ||
        (item.standardName || "").toLowerCase().includes(searchLower) ||
        (item.numId || "").toLowerCase().includes(searchLower);

      // 递归检查子项
      let matchingChildren: MenuItem[] = [];
      if (item.children && item.children.length > 0) {
        matchingChildren = searchInItems(item.children);
      }

      // 如果当前项或子项匹配
      if (nameMatch || matchingChildren.length > 0) {
        return {
          ...item,
          children: matchingChildren,
        };
      }

      return false;
    });
  };

  return searchInItems(props.menuList);
});

const handleSelect = (item: MenuItem) => {
  if (!item || !item.type) {
    console.warn("未点击菜单项:", item);
    return;
  }

  // 确保更新选中项时重置编辑状态
  const updatedItem = {
    ...item,
    isEdit: false,
  };

  emit("update:menu-temp", updatedItem);
};

const handleSave = async () => {
  try {
    // 添加表单验证
    if (!props.menuTemp.name?.trim()) {
      ElMessage.warning("请填写必填字段");
      return;
    }

    // 统一保存入口
    await emit("menu-action", {
      type: "save",
      data: props.menuTemp, // 传递最新数据
    });

    emit("menu-action", { type: "refresh" });
  } catch (error) {
    console.error("保存失败:", error);
    ElMessage.error("保存失败");
  }
};

const handleCancel = () => {
  emit("menu-action", { type: "cancel" });
};

const handleRemoveNewItem = async (id: string) => {
  try {
    await emit("menu-action", { type: "remove", id });
    // 触发刷新
    emit("menu-action", { type: "refresh" });
  } catch (error) {
    console.error("删除失败:", error);
    ElMessage.error("删除失败");
  }
};

const updateInputName = (value: string) => {
  emit("update:menu-temp", {
    ...props.menuTemp,
    name: value,
    isEdit: true, // 确保编辑状态正确
  });
};

// 然后定义所有的方法
const handleMiniMenuAction = async ({
  type,
  data,
}: {
  type: string;
  data?: any;
}) => {
  try {
    if (!currentContextItem.value) {
      console.error("没有选择指标项");
      return;
    }

    switch (type) {
      case "insert":
        // 更新 menuTemp 的插入标记和关联编号
        const updatedTemp = {
          ...props.menuTemp,
          insertBefore: true,
          relativeNumId: currentContextItem.value?.numId, // 从右键菜单项获取 numId
        };
        emit("update:menu-temp", updatedTemp);

        // 触发插入操作
        await emit("menu-action", {
          type: "insert-sibling",
          data: updatedTemp, // 传递更新后的数据
        });
        break;
      case "addSibling":
        await emit("menu-action", {
          type: "add-sibling",
          data: props.menuTemp,
        });
        break;

      case "addChild":
        await emit("menu-action", {
          type: "add-child",
          data: props.menuTemp,
        });
        break;

      case "edit":
        // 先更新 menuTemp
        await emit("update:menu-temp", {
          ...currentContextItem.value,
          isEdit: true,
        });

        // 然后触发编辑操作
        const result = await emit("menu-action", {
          type: "edit",
          data: currentContextItem.value,
        });
        if (result) {
          emit("menu-action", { type: "refresh" });
        }
        break;

      case "del":
        if (!data?.id) {
          console.error("删除操作缺少ID:", data);
          return;
        }

        const newMenuList = [...props.menuList];
        const removeDeletedItem = (items: MenuItem[]): boolean => {
          for (let i = 0; i < items.length; i++) {
            if (items[i].id === data.id) {
              items.splice(i, 1);
              return true;
            }
            if (items[i].children?.length) {
              if (removeDeletedItem(items[i].children)) {
                return true;
              }
            }
          }
          return false;
        };

        if (removeDeletedItem(newMenuList)) {
          await emit("menu-action", {
            type: "post-delete",
            data: { deletedId: data.id },
          });
        }
        emit("menu-action", { type: "refresh" });
        break;
    }
  } catch (error) {
    console.error("操作失败:", error);
    ElMessage.error("操作失败");
  }
};

// 添加默认展开的菜单项
const defaultOpeneds = computed(() => {
  const opens: string[] = [];
  const findParents = (items: MenuItem[]) => {
    items.forEach((item) => {
      if (item.children?.length) {
        opens.push(item.id);
        findParents(item.children);
      }
    });
  };
  findParents(props.menuList);
  return opens;
});

// 在 script setup 中添加
const handleRefresh = async () => {
  await emit("menu-action", { type: "refresh" });
};

// 添加刷新数据处理方法
const handleRefreshData = async ({
  id,
  newName,
}: {
  id: string;
  newName: string;
}) => {
  // 查找并更新指定项
  const updateItem = (items: MenuItem[]): boolean => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === id) {
        items[i].name = newName;
        items[i].isEdit = false;
        return true;
      }
      if (items[i].children?.length) {
        if (updateItem(items[i].children)) {
          return true;
        }
      }
    }
    return false;
  };
  emit("menu-action", { type: "refresh" });

  // 更新菜单数据
  updateItem(props.menuList);

  // 可选：重新获取最新数据
  handleRefresh();
};
// 增强搜索功能
const searchInContent = (item: MenuItem) => {
  return [
    item.name,
    item.content,
    item.numId,
    ...(item.terms || []).map((t) => t.cName),
  ]
    .join(" ")
    .toLowerCase()
    .includes(searchText.value.toLowerCase());
};

// 添加 handleUpdateItem 方法
const handleUpdateItem = (updatedItem: MenuItem) => {
  // 更新菜单列表中的项
  const updateItemInList = (items: MenuItem[]): boolean => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === updatedItem.id) {
        items[i] = { ...items[i], ...updatedItem };
        return true;
      }
      if (items[i].children?.length) {
        if (updateItemInList(items[i].children)) {
          return true;
        }
      }
    }
    return false;
  };

  const newMenuList = [...props.menuList];
  if (updateItemInList(newMenuList)) {
    emit("update:menu-list", newMenuList);
  }

  // 如果更新的是当前选中项，也更新 menuTemp
  if (props.menuTemp.id === updatedItem.id) {
    emit("update:menu-temp", { ...updatedItem });
  }
};
// 添加动态高度计算
// const maxMenuHeight = ref(400) // 默认高度
// onMounted(() => {
//   maxMenuHeight.value = window.innerHeight - 100 // 根据视口动态计算
// })
// 在 setup 中添加
const menuRef = ref<HTMLElement | null>(null);
</script>

<style scoped>
.sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.search-box {
  padding: 16px;
}

.action-box {
  padding: 0 16px 16px;
}

.menu-container {
  position: relative;
  overflow: visible; /* 允许菜单溢出 */
}

.el-menu {
  border-right: none;
}

:deep(.el-menu-item) {
  height: 40px;
  line-height: 40px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.sidebar-container {
  height: 100%; /* 填充父容器高度 */
  overflow: hidden; /* 避免额外滚动 */
}

.nav-sidebar {
  /* height: auto !important; */
  height: 20px; /* 限制最大高度为 400px */
  /* overflow-y: auto !important; */
  background-color: #f1f8e9; /* 浅绿色背景 */
  padding: 20px;
  border-right: 1px solid #dcedc8;
}

.nav-sidebar::-webkit-scrollbar {
  width: 8px; /* 滚动条宽度 */
  background: #f5f5f5; /* 滚动条背景颜色 */
}

.nav-sidebar::-webkit-scrollbar-thumb {
  background-color: #c1c1c1; /* 滚动条颜色（灰色） */
  border-radius: 4px; /* 滚动条圆角 */
  border: 2px solid transparent; /* 滚动条边距 */
  background-clip: content-box; /* 使滚动条内边距生效 */
}

.nav-sidebar::-webkit-scrollbar-thumb:hover {
  background-color: #a8a8a8; /* 鼠标悬浮时的滚动条颜色 */
}

.nav-sidebar::-webkit-scrollbar-track {
  background: #f1f1f1; /* 滚动条轨道颜色 */
}
</style>
